import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress, Modal, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getMemberEngagement } from '../../api/memberEngagement';
import UserProfileMainPage from '../../components/user-profile-main/UserProfileMainPage';
import MemberEngagementDrawer from './MemberEngagementDrawer';
import scopedCss from './styles';
import {
  planCls,
  monthlyOf,
  money,
  toINR,
  fmtMon,
  fmtDur,
  ago,
  median,
  firstReplyLatency,
  metrics,
  outboundConvos,
} from './memberEngagementUtils';
import {
  subPill,
  Avatar,
  statusPill,
  SearchIcon,
  profileModalStyle,
} from './memberEngagementUi';

const SORT_PRESETS = {
  risk: ['risk', 'asc'],
  rate: ['rate', 'desc'],
  slow: ['med', 'desc'],
  active: ['active', 'desc'],
  sent: ['sent', 'desc'],
  name: ['name', 'asc'],
};
const presetFor = (key, dir) => {
  for (const v in SORT_PRESETS) {
    if (SORT_PRESETS[v][0] === key && SORT_PRESETS[v][1] === dir) return v;
  }
  return 'custom';
};
const sortVal = (c, key) => {
  const k = c.k;
  const m = c.m;
  switch (key) {
    case 'name': return m.name.toLowerCase();
    case 'sent': return k.sent;
    case 'reached': return k.reached;
    case 'replies': return k.replies;
    case 'rate': return k.rate;
    case 'med': return k.med == null ? Infinity : k.med;
    // Members with no conversations have no status; they sort after every real
    // one rather than leading an "at risk first" list they don't belong on.
    case 'risk': return k.known ? { risk: 0, warn: 1, good: 2 }[k.status] : 3;
    case 'active': return k.last;
    default: return 0;
  }
};

const MemberEngagement = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('All');
  const [sortKey, setSortKey] = useState('risk');
  const [sortDir, setSortDir] = useState('asc');
  const [query, setQuery] = useState('');
  const [drawerId, setDrawerId] = useState(null);
  const [profileUserId, setProfileUserId] = useState(null);

  const openProfile = (e, userId) => {
    if (e) e.stopPropagation();
    if (userId) setProfileUserId(userId);
  };
  const closeProfile = () => setProfileUserId(null);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      // Load the whole tracked population once; country (and the search / status /
      // sort controls) all narrow the list client-side, so switching country never
      // needs a round-trip.
      const res = await getMemberEngagement({});
      if (res) setData(res);
    } catch (err) {
      console.error('Error loading member engagement', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const now = useMemo(() => (data?.now ? new Date(data.now) : new Date()), [data?.now]);
  const members = useMemo(() => data?.members || [], [data?.members]);
  const computed = useMemo(() => members.map((m) => ({ m, k: metrics(m) })), [members]);

  // Country options for the filter: only countries that actually have members,
  // each with its member count, sorted by count (largest first) — the same shape
  // the Messaging Activity country dropdown uses. Built from the loaded population
  // so the counts always match what the list can show.
  const countryFacet = useMemo(() => {
    const counts = new Map();
    members.forEach((m) => {
      const c = (m.country || '').trim();
      if (c) counts.set(c, (counts.get(c) || 0) + 1);
    });
    return [...counts.entries()]
      .map(([name, n]) => ({ name, n }))
      .sort((a, b) => b.n - a.n || a.name.localeCompare(b.name));
  }, [members]);

  // The selected country scopes the WHOLE page (KPIs, health split and table), the
  // same way Messaging Activity's country filter reframes every figure — not just
  // the rows. The facet above is always built from the full population so every
  // country stays selectable.
  const countryScoped = useMemo(
    () => (countryFilter === 'All'
      ? computed
      : computed.filter((c) => (c.m.country || '').trim() === countryFilter)),
    [computed, countryFilter],
  );

  const kpis = useMemo(() => {
    const n = countryScoped.length;
    // Average over members who HAVE conversations only. A member with 0 matches
    // messaged has no response rate (0/0 is undefined, not 0%) — counting them as 0
    // dragged this KPI down with members who never had the chance to get a reply.
    // Deleted members land here too: their chats are hard-deleted, so their rate is
    // unknowable rather than zero.
    const rated = countryScoped.filter((c) => c.k.reached > 0);
    const avgRate = rated.length
      ? Math.round((rated.reduce((s, c) => s + c.k.rate, 0) / rated.length) * 100)
      : 0;
    const allLats = [];
    // Outbound threads only — this is "how fast do matches reply to our members",
    // and an inbound thread the member never answered has no such latency to
    // contribute. (It reports null today, so this is belt-and-braces rather than a
    // behaviour change; stated explicitly so the intent survives the next edit.)
    countryScoped.forEach((c) => outboundConvos(c.m).forEach((cv) => {
      const l = firstReplyLatency(cv);
      if (l !== null) allLats.push(l);
    }));
    const medReply = median(allLats);
    // The health split is over members whose rate EXISTS. A subscriber who has
    // messaged nobody is not "at risk" of being ignored — nobody has had the
    // chance to ignore them. They are counted separately as `unknown`.
    const g = rated.filter((c) => c.k.status === 'good').length;
    const w = rated.filter((c) => c.k.status === 'warn').length;
    const riskMembers = rated.filter((c) => c.k.status === 'risk');
    const risk = riskMembers.length;
    // Revenue AT RISK is money that could still be lost, so it counts only members
    // who are actually paying today: subscription Active and the account not
    // deleted. A cancelled / expired / deleted at-risk member's revenue is already
    // gone — booking it here reported churn that has happened as churn that might
    // (on 25 Jul 2026 the whole ₹8,550 figure was one cancelled, deleted member).
    const payingAtRisk = riskMembers.filter((c) => c.m.sub.status === 'active' && !c.m.isDeletedUser);
    const atRiskINR = payingAtRisk.reduce((a, c) => a + toINR(monthlyOf(c.m.sub), c.m.sub?.currency), 0);
    // Subscribers with no outbound conversation at all — listed (they are on the
    // Subscription Report) but outside every response figure on the page.
    const unknown = n - rated.length;
    return { n, avgRate, medReply, g, w, risk, atRiskINR, atRiskPaying: payingAtRisk.length, rated: rated.length, unknown };
  }, [countryScoped]);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    const out = countryScoped.filter((c) => {
      // `none` = subscribers with no conversation to score. Every other health
      // filter only ever matches members whose rate exists.
      if (filter === 'none') { if (c.k.known) return false; }
      else if (filter !== 'all' && (!c.k.known || c.k.status !== filter)) return false;
      if (q && !c.m.name.toLowerCase().includes(q)) return false;
      return true;
    });
    out.sort((a, b) => {
      const va = sortVal(a, sortKey);
      const vb = sortVal(b, sortKey);
      let r = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      if (r !== 0) return sortDir === 'asc' ? r : -r;
      return a.m.name.localeCompare(b.m.name);
    });
    return out;
  }, [countryScoped, filter, query, sortKey, sortDir]);

  const openDrawer = (id) => setDrawerId(id);

  const onHeaderSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' || key === 'risk' ? 'asc' : 'desc');
    }
  };

  const arrow = (key) => (key === sortKey ? (sortDir === 'asc' ? '↑' : '↓') : '↕');

  const { n, avgRate, medReply, g, w, risk, atRiskINR, atRiskPaying, rated, unknown } = kpis;
  // The split is a share of members whose response rate EXISTS (`rated`), not of
  // everyone listed — otherwise the three segments would silently fail to fill the
  // bar by however many subscribers have messaged nobody.
  const gPct = rated ? Math.round((g / rated) * 100) : 0;
  const wPct = rated ? Math.round((w / rated) * 100) : 0;
  const rPct = rated ? Math.round((risk / rated) * 100) : 0;

  const drawerEntry = drawerId ? computed.find((c) => c.m.id === drawerId) : null;

  return (
    <div className="met-scope">
      <style>{scopedCss}</style>

      <main className="page">
        {isLoading && !data ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
            <CircularProgress sx={{ color: '#E8392E' }} />
          </div>
        ) : (
          <div style={{ opacity: isLoading ? 0.6 : 1, transition: 'opacity .2s' }}>
            <section className="kpis">
              {/* `data.total` is the real number of tracked subscribers; the server
                  caps how many it ships. Show the cap plainly rather than let a
                  shortened list read as the whole population. */}
              <div className="kpi"><div className="lab">Subscribers tracked</div><div className="val">{n}</div><div className="note">{data?.truncated ? `Showing ${n} of ${data.total} — narrow the filters to see the rest` : `Every member on the Subscription Report${unknown ? ` · ${unknown} have messaged nobody yet` : ''}`}</div></div>
              {/* The value is the MEAN OF EACH MEMBER'S RATE, not the pooled
                  replied ÷ messaged the caption used to claim — the two differ
                  (60% vs 51% on 25 Jul 2026) because members with many matches
                  weigh more in a pooled ratio. Averaging per member is the right
                  read for a member-level page; the caption now says so. */}
              <div className="kpi"><div className="lab">Avg response rate</div><div className="val">{avgRate}<small>%</small></div><div className="note">Average across subscribers of their own matches-replied ÷ matches-messaged</div></div>
              <div className="kpi"><div className="lab">Median time to 1st reply</div><div className="val">{medReply != null ? fmtDur(medReply) : '—'}</div><div className="note">Across all conversations that got a reply, bots included</div></div>
              <div className="kpi alert"><div className="lab">At-risk subscribers</div><div className="val">{risk}</div><div className="note">Under 30% of their matches reply</div></div>
              <div className="kpi alert"><div className="lab">Revenue at risk</div><div className="val">{money(atRiskINR, 'INR')}<small>/mo</small></div><div className="note">{`Monthly value of the ${atRiskPaying} at-risk ${atRiskPaying === 1 ? 'member' : 'members'} still on an active plan (INR)`}</div></div>
            </section>

            <section className="health">
              <div className="top">
                <span className="lab">Response health across {rated} subscriber{rated === 1 ? '' : 's'} who have messaged someone</span>
                <span className="legend">
                  <span><i className="seg-good" />Getting replies · {g} ({gPct}%)</span>
                  <span><i className="seg-warn" />Low response · {w} ({wPct}%)</span>
                  <span><i className="seg-risk" />At risk · {risk} ({rPct}%)</span>
                  {unknown > 0 && <span style={{ color: 'var(--soft)' }}>Not measurable · {unknown}</span>}
                </span>
              </div>
              <div className="bar">
                <span className="seg-good" style={{ width: `${gPct}%` }} title={`Getting replies — ${g} of ${rated} subscribers (${gPct}%)`}>{gPct >= 7 ? `${gPct}%` : ''}</span>
                <span className="seg-warn" style={{ width: `${wPct}%` }} title={`Low response — ${w} of ${rated} subscribers (${wPct}%)`}>{wPct >= 7 ? `${wPct}%` : ''}</span>
                <span className="seg-risk" style={{ width: `${rPct}%` }} title={`At risk — ${risk} of ${rated} subscribers (${rPct}%)`}>{rPct >= 7 ? `${rPct}%` : ''}</span>
              </div>
            </section>

            <div className="controls">
              <div className="search">
                <SearchIcon />
                <input
                  type="search"
                  placeholder="Search a member by name…"
                  aria-label="Search members"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <div className="chips" role="group" aria-label="Filter by response health">
                <button className="chip" aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>All <span className="n">{n}</span></button>
                <button className="chip" aria-pressed={filter === 'good'} onClick={() => setFilter('good')}>Getting replies <span className="n">{g}</span></button>
                <button className="chip" aria-pressed={filter === 'warn'} onClick={() => setFilter('warn')}>Low <span className="n">{w}</span></button>
                <button className="chip" aria-pressed={filter === 'risk'} onClick={() => setFilter('risk')}>At risk <span className="n">{risk}</span></button>
                {unknown > 0 && <button className="chip" aria-pressed={filter === 'none'} onClick={() => setFilter('none')}>Messaged nobody <span className="n">{unknown}</span></button>}
              </div>
              <select
                className="sort"
                aria-label="Filter by country"
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
              >
                <option value="All">All countries</option>
                {countryFacet.map((c) => (
                  <option key={c.name} value={c.name}>{c.name} ({c.n})</option>
                ))}
              </select>
              <select
                className="sort"
                aria-label="Sort members"
                value={presetFor(sortKey, sortDir)}
                onChange={(e) => {
                  const p = SORT_PRESETS[e.target.value];
                  if (p) { setSortKey(p[0]); setSortDir(p[1]); }
                }}
              >
                <option value="risk">Sort: At risk first</option>
                <option value="rate">Response rate (high→low)</option>
                <option value="slow">Slowest replies first</option>
                <option value="active">Most recently active</option>
                <option value="sent">Most messages sent</option>
                <option value="name">Name (A→Z)</option>
                <option value="custom" hidden>Custom (column)</option>
              </select>
            </div>

            {/* The dot is PRESENCE, not account status — see the note on `onlineDot`.
                Account status has its own surfaces (the DELETED tag on the name and
                the drawer's Account pill) and must never be read off this dot. */}
            <div className="legendline">
              <span style={{ fontWeight: 600, color: 'var(--ink)' }}>Presence</span>
              <span className="k"><i style={{ background: 'var(--good)' }} />Online now</span>
              <span style={{ color: 'var(--faint)' }}>— a green dot on the avatar. No dot means offline; it does not say anything about the account.</span>
            </div>

            <div className="tablewrap stickyhead">
              <div className="scroll">
                <table>
                  <thead>
                    <tr>
                      {[
                        ['name', 'Subscriber', false],
                      ].map(([key, label]) => (
                        <th key={key} className={`sortable ${sortKey === key ? 'active-sort' : ''}`} onClick={() => onHeaderSort(key)}>{label}<span className="ar">{arrow(key)}</span></th>
                      ))}
                      <th>Plan</th>
                      {[
                        ['sent', 'Sent', true],
                        ['reached', 'Reached', true],
                        ['replies', 'Replies', true],
                      ].map(([key, label]) => (
                        <th key={key} className={`num sortable ${sortKey === key ? 'active-sort' : ''}`} onClick={() => onHeaderSort(key)}>{label}<span className="ar">{arrow(key)}</span></th>
                      ))}
                      {[
                        ['rate', 'Response rate'],
                        ['med', 'Median 1st reply'],
                        ['risk', 'Status'],
                        ['active', 'Last active'],
                      ].map(([key, label]) => (
                        <th key={key} className={`sortable ${sortKey === key ? 'active-sort' : ''}`} onClick={() => onHeaderSort(key)}>{label}<span className="ar">{arrow(key)}</span></th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {list.map(({ m, k }) => {
                      // "Unknown" is not "zero": a member deleted before their chats were
                      // archived has none to recompute from, and a member with 0 matches
                      // messaged has no rate to compute (0/0). Both render "—" rather than a
                      // red 0% that asserts they never replied. A member deleted since
                      // archiving shipped still has their transcript, so they read normally.
                      const chatLost = m.isDeletedUser && !m.convos.length;
                      const rateKnown = k.known && !chatLost;
                      // Listed but never messaged anyone — every response figure is
                      // unknown, and saying so is the point of the row.
                      const neverMessaged = !k.known && !chatLost;
                      const fillCls = k.status === 'good' ? 'good' : k.status === 'warn' ? 'warn' : 'risk';
                      const rate = Math.round(k.rate * 100);
                      return (
                        <tr key={m.id} tabIndex={0} onClick={() => openDrawer(m.id)}>
                          <td>
                            <div className="member">
                              <Avatar src={m.profilePicture} initials={m.initials} online={m.online} />
                              <div>
                                <div className="m-name">{m.name}{m.isDeletedUser && <span className="del-tag" title="Account deleted — subscription still counted">deleted</span>}</div>
                                <div className="m-sub">{neverMessaged ? 'has messaged nobody' : `${k.replied}/${k.reached} matches replied`} · <span className="vprofile" role="button" tabIndex={0} onClick={(e) => openProfile(e, m.id)}>View profile →</span></div>
                              </div>
                            </div>
                          </td>
                          <td>
                            {/* `m.plan` is the plan KEY (lux | gold | goldF) and only
                                drives the chip colour; `m.planName` is the label. The
                                key used to BE the label, which is why this column read
                                "DIAMOND" and "PLATINUM" — tiers that do not exist. */}
                            <span className={`plan ${planCls(m.plan)}`}>{m.planName || m.plan}</span>
                            <div className="t-price">{money(monthlyOf(m.sub), m.sub.currency)}/mo</div>
                            <div className="t-price">since {fmtMon(m.sub.since)}</div>
                            <div style={{ marginTop: 6 }}>{subPill(m.sub.status)}</div>
                          </td>
                          <td className="num">{k.sent}</td>
                          <td className="num">{k.reached}</td>
                          <td className="num">{k.replies}</td>
                          <td>
                            {rateKnown ? (
                              <div className="meter">
                                <span className="track"><span className={`fill ${fillCls}`} style={{ width: `${Math.max(rate, 2)}%` }} /></span>
                                <span className="pct">{rate}%</span>
                              </div>
                            ) : (
                              <span style={{ color: 'var(--soft)' }} title={chatLost ? 'Unavailable — chat history was permanently deleted with this account' : 'This member has not messaged anyone, so there is no response rate to calculate'}>—</span>
                            )}
                          </td>
                          {/* "no reply" is a finding about a member who DID message
                              people; one who messaged nobody gets a dash instead. */}
                          <td>{!chatLost && k.med != null ? fmtDur(k.med) : (chatLost || neverMessaged ? <span style={{ color: 'var(--soft)' }}>—</span> : <span style={{ color: 'var(--accent)', fontWeight: 600 }}>no reply</span>)}</td>
                          <td>{rateKnown ? statusPill(k.status) : <span style={{ color: 'var(--soft)' }}>—</span>}</td>
                          <td><span style={{ color: 'var(--soft)' }}>{k.last > 0 ? ago(new Date(k.last).toISOString(), now) : '—'}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {list.length === 0 && <div className="empty">No members match these filters.</div>}
            </div>

            <p className="foot"><b>Internal tool.</b> Message content is shown for response-quality and safety review only. Keep access, retention, and member consent aligned with your privacy policy and terms of service.</p>
          </div>
        )}

      </main>

      <MemberEngagementDrawer
        member={drawerEntry ? drawerEntry.m : null}
        now={data?.now}
        onClose={() => setDrawerId(null)}
      />

      <Modal open={!!profileUserId} onClose={closeProfile} aria-labelledby="member-profile-modal" sx={{ zIndex: 1500 }}>
        <Box sx={profileModalStyle}>
          <Box
            sx={{
              position: 'sticky',
              top: '0px',
              backgroundColor: 'white',
              zIndex: 10,
              padding: '10px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <IconButton sx={{ position: 'absolute', top: 10, right: 0 }} onClick={closeProfile}>
              <CloseIcon />
            </IconButton>
          </Box>
          {profileUserId && <UserProfileMainPage userId={profileUserId} />}
        </Box>
      </Modal>
    </div>
  );
};

export default MemberEngagement;
